import { NikePlayerProps, NikePlayerSource } from './videojs/types';
import React, { ComponentProps, lazy, memo, Suspense, SuspenseProps, useCallback, useEffect, useMemo } from 'react';
import {
    allowFullscreenStream,
    allowPauseStream,
    disableRemotePlaybackStream,
    isSupportedStreamPlayerType,
    resolveStreamPlayerType,
    StreamPlayerType
} from '../utils';
import { getPlayerVolumeState } from '../persistance';
import { isBoolean, isEmpty, isFunction, isNumber } from 'lodash';
import { NikePlayerError } from './NikePlayerError';
import { useStateWithPrevious } from '../../../common/hooks/usePreviousValue';
import { ErrorBoundary } from '../../../common/ErrorBoundary';
import './NikePlayer.less';
import { clientLog } from '../../../common/logging/logs';
import { StreamLog } from '../../../common/logging/StreamLog';
import { LEVEL } from '../../../common/logging/LEVEL';

const VideoJsPlayer = lazy(() =>
    import(/* webpackChunkName: "chunk.nike-player-videojs" */ './videojs/VideoJsPlayerLoadable')
);

const YoutubePlayer = lazy(() => import(/* webpackChunkName: "chunk.nike-player-youtube" */ './youtube/YoutubePlayer'));
const IframeEmbedPlayer = lazy(() =>
    import(/* webpackChunkName: "chunk.nike-player-iframe" */ './embed/IframeEmbedPlayer')
);

const NikePlayerCore = ({
    className,
    'data-atid': dataAtid = 'video-player',
    autoplay,
    allowPause,
    allowFullScreen,
    disableRemotePlayback,
    initialMuted,
    initialVolume,
    source,
    onPlay,
    onReady,
    onDispose,
    onError,
    onEnd,
    onPause,
    fluid,
    fill,
    trackVideoData
}: NikePlayerProps) => {
    const { handleError, showDefaultErrorUI } = useDefaultErrorHandler({ source, onError });
    const streamPlayerType = resolveStreamPlayerType(source?.providerName, source?.src);

    if (isEmpty(source)) {
        return <NikePlayerError debug={{ reason: 'DEFAULT_ERROR_UI', source, streamPlayerType }} fill={fill} />;
    }

    const isShowUnsupportedErrorUI = showUnsupportedErrorUI(streamPlayerType, source);

    if (isShowUnsupportedErrorUI) {
        return (
            <NikePlayerError
                debug={{ reason: 'UNSUPPORTED_STREAM_PLAYER_TYPE', source, streamPlayerType }}
                fill={fill}
            />
        );
    }

    if (showDefaultErrorUI) {
        return <NikePlayerError debug={{ reason: 'DEFAULT_ERROR_UI', source, streamPlayerType }} fill={fill} />;
    }

    if (!isBoolean(autoplay)) {
        autoplay = true;
    }

    if (!isBoolean(initialMuted) || !isNumber(initialVolume)) {
        const playerVolumeState = getPlayerVolumeState();
        initialVolume = playerVolumeState.volume;
        initialMuted = playerVolumeState.muted;
    }

    if (!isBoolean(allowFullScreen)) {
        allowFullScreen = allowFullscreenStream(source?.providerName);
    }
    if (!isBoolean(allowPause)) {
        allowPause = allowPauseStream(source?.providerName);
    }
    if (!isBoolean(allowPause)) {
        disableRemotePlayback = disableRemotePlaybackStream(source?.providerName);
    }

    return (
        <div className={className} data-atid={dataAtid} data-provider={source?.providerName}>
            {streamPlayerType === 'HLS' && (
                <VideoJsPlayer
                    source={source}
                    autoplay={autoplay}
                    onPlay={onPlay}
                    onEnd={onEnd}
                    onPause={onPause}
                    onError={handleError}
                    onReady={onReady}
                    onDispose={onDispose}
                    allowFullScreen={allowFullScreen}
                    allowPause={allowPause}
                    disableRemotePlayback={disableRemotePlayback}
                    initialMuted={initialMuted}
                    initialVolume={initialVolume}
                    fluid={fluid}
                    fill={fill}
                    trackVideoData={trackVideoData}
                />
            )}

            {streamPlayerType === 'YOUTUBE' && (
                <YoutubePlayer
                    src={source?.src}
                    autoplay={autoplay}
                    onPlay={onPlay}
                    onEnd={onEnd}
                    onPause={onPause}
                    onError={handleError}
                    muted={initialMuted}
                    onReady={onReady}
                    allowFullScreen={allowFullScreen}
                    fill={fill}
                    trackVideoData={trackVideoData}
                />
            )}

            {streamPlayerType === 'EMBED' && (
                <IframeEmbedPlayer
                    provider={source?.providerName}
                    src={source?.src}
                    onAppear={onPlay}
                    onReady={onReady}
                    fill={fill}
                    trackVideoData={trackVideoData}
                />
            )}
        </div>
    );
};

export const NikePlayer = memo(function NikePlayer({
    fallback = <div />,
    source,
    ...rest
}: ComponentProps<typeof NikePlayerCore> & {
    fallback?: SuspenseProps['fallback'];
}) {
    return (
        <ErrorBoundary description={'nike-player sources :' + JSON.stringify(source)}>
            <Suspense fallback={fallback}>
                <NikePlayerCore source={source} {...rest} />
            </Suspense>
        </ErrorBoundary>
    );
});

const showUnsupportedErrorUI = (streamPlayerType: StreamPlayerType, source: NikePlayerSource) => {
    const supported = isSupportedStreamPlayerType(streamPlayerType);
    return !supported && source?.src;
};

/**
 * Riesi, ci sa ma zobrazit defaultna errorova hlaska.
 * Resetuje error stav pri zmene source.
 */
const useDefaultErrorHandler = ({ source, onError }: Pick<NikePlayerProps, 'source' | 'onError'>) => {
    const [isError, setError, previousIsError] = useStateWithPrevious(false);
    const hasCustomErrorHandler = isFunction(onError);

    const handleError = useCallback(
        (event = {}, error = {}) => {
            if (source.providerName === 'YOUTUBE') {
                // Show default YouTube error - This video is age-restricted and only available on YouTube.
                if (event.data === '150') {
                    return;
                }
            }

            clientLog(
                StreamLog({
                    message: error?.message || event?.type || 'Unknown Error',
                    level: LEVEL.ERROR,
                    data: { error, source, event: { type: event?.type } }
                })
            );

            if (!hasCustomErrorHandler) {
                setError(true);
            } else {
                onError(event, error);
            }
        },
        [hasCustomErrorHandler, onError, setError, source]
    );

    useEffect(() => {
        if (!hasCustomErrorHandler) {
            return;
        }
        // mame error, a zmenu nesposobil setState (setError)
        if (isError && isError === previousIsError) {
            setError(false);
        }
    }, [previousIsError, isError, source, setError, hasCustomErrorHandler]);

    return useMemo(() => ({ handleError, showDefaultErrorUI: isError && !hasCustomErrorHandler }), [
        handleError,
        hasCustomErrorHandler,
        isError
    ]);
};
